import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function AdminUsersManager() {
    const { authFetch, user } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [creating, setCreating] = useState(false);
    const [createdInfo, setCreatedInfo] = useState(null);

    const [editingId, setEditingId] = useState(null);
    const [editEmail, setEditEmail] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await authFetch('/api/admin-users');
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Fehler beim Laden');
            setAdmins(data.data || []);
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCreate = async (e) => {
        e.preventDefault();
        setError('');
        setCreating(true);
        try {
            const res = await authFetch('/api/admin-users', {
                method: 'POST',
                body: { username: newUsername.trim(), email: newEmail.trim() }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Anlegen fehlgeschlagen');
            setCreatedInfo({ username: newUsername.trim(), email: newEmail.trim(), tempPassword: data.tempPassword });
            setNewUsername('');
            setNewEmail('');
            setShowCreate(false);
            await load();
        } catch (err) {
            setError(err.message);
        }
        setCreating(false);
    };

    const handleEdit = (admin) => {
        setEditingId(admin.id);
        setEditEmail(admin.email || '');
    };

    const handleSaveEdit = async (id) => {
        setError('');
        try {
            const res = await authFetch(`/api/admin-users/${id}`, {
                method: 'PUT',
                body: { email: editEmail.trim() }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen');
            setEditingId(null);
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (admin) => {
        if (!window.confirm(`Admin "${admin.username}" wirklich loeschen?`)) return;
        setError('');
        try {
            const res = await authFetch(`/api/admin-users/${admin.id}`, { method: 'DELETE' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Loeschen fehlgeschlagen');
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0">Admin-Benutzer</h3>
                <button
                    className="btn btn-primary"
                    onClick={() => { setShowCreate(!showCreate); setCreatedInfo(null); }}
                >
                    {showCreate ? 'Abbrechen' : '+ Neuer Admin'}
                </button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {createdInfo && (
                <div className="alert alert-success">
                    <strong>Admin angelegt:</strong> {createdInfo.username} ({createdInfo.email})
                    <br />
                    Es wurde eine E-Mail mit einem Link zum Setzen des Passworts versendet (24h gueltig).
                    <br />
                    <small className="text-muted">
                        Fallback-Initialpasswort (falls keine Mail ankommt): <code>{createdInfo.tempPassword}</code> &mdash; der Admin wird beim ersten Login zur Aenderung gezwungen.
                    </small>
                </div>
            )}

            {showCreate && (
                <div className="card mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">Neuen Admin anlegen</h5>
                        <form onSubmit={handleCreate}>
                            <div className="row g-2">
                                <div className="col-md-5">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Benutzername"
                                        value={newUsername}
                                        onChange={(e) => setNewUsername(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-md-5">
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder="E-Mail"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-success w-100" disabled={creating}>
                                        {creating ? 'Anlegen...' : 'Anlegen'}
                                    </button>
                                </div>
                            </div>
                        </form>
                        <small className="text-muted d-block mt-2">
                            Der neue Admin erhaelt eine E-Mail mit einem Link, ueber den er sein Passwort selbst setzt.
                        </small>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary"></div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Benutzername</th>
                                <th>E-Mail</th>
                                <th>Status</th>
                                <th>Angelegt</th>
                                <th style={{ width: '180px' }}>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.length === 0 && (
                                <tr><td colSpan="5" className="text-center text-muted py-4">Keine Admins vorhanden</td></tr>
                            )}
                            {admins.map(admin => (
                                <tr key={admin.id}>
                                    <td>
                                        <strong>{admin.username}</strong>
                                        {user?.userId === admin.id && <span className="badge bg-info ms-2">Du</span>}
                                    </td>
                                    <td>
                                        {editingId === admin.id ? (
                                            <input
                                                type="email"
                                                className="form-control form-control-sm"
                                                value={editEmail}
                                                onChange={(e) => setEditEmail(e.target.value)}
                                            />
                                        ) : (
                                            admin.email || <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {admin.must_change_password
                                            ? <span className="badge bg-warning text-dark">Passwort offen</span>
                                            : <span className="badge bg-success">Aktiv</span>}
                                    </td>
                                    <td className="text-muted small">
                                        {admin.created_at ? new Date(admin.created_at).toLocaleDateString('de-DE') : '—'}
                                    </td>
                                    <td>
                                        {editingId === admin.id ? (
                                            <>
                                                <button
                                                    className="btn btn-sm btn-success me-1"
                                                    onClick={() => handleSaveEdit(admin.id)}
                                                >
                                                    Speichern
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => setEditingId(null)}
                                                >
                                                    Abbrechen
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                    onClick={() => handleEdit(admin)}
                                                >
                                                    E-Mail
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(admin)}
                                                    disabled={user?.userId === admin.id}
                                                    title={user?.userId === admin.id ? 'Du kannst dich nicht selbst loeschen' : ''}
                                                >
                                                    Loeschen
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AdminUsersManager;
